import { Fragment, useEffect, useMemo, useRef } from "react";
import { TypeInventory } from "~/common/entities/general";
import { AppLink, formatCurrency } from "@commons-monorepo/index";
import { isSafari } from "@commons-monorepo/index";
import Breadcrumbs from "~/components/breadcrumbs/breadcrumbs";
import ReadMore from "~/components/ReadMore/ReadMore";
import { Reviews } from "~/components/Reviews/Reviews";
import { SliderProducts } from "~/components/slider-products";
import { StoreInfo } from "~/components/StoreInfo/StoreInfo";
import { ProductInfoSource } from "~/components/StoreInfo/types";
import type { DBProduct } from "@commons-monorepo/index";
import { getDbProductId } from "~/modules/cart/utils";
import { productPrice } from "~/modules/cart/utils/product-cart.utils";
import { TemplateType } from "@commons-monorepo/index";
import Toppings from "../../components/Toppings";
import { useLogicProduct } from "../../product.hook";
import { ProductProps } from "../../Product.type";
import ProductCard from "./components/product-card";
import { ProductCloudPrices } from "./components/product-cloud-prices/product-cloud-prices";
import { ProductCloudTags } from "./components/product-cloud-tags/product-cloud-tags";
import { ProductCloudTitle } from "./components/product-cloud-title/product-cloud-title";
import { ProductSelector } from "~/components/product-selector";
import { useAppParams } from "@commons-monorepo/index";
import SchemaGraph from "../../components/schema-graph";
import styles from "./product-skin-1.module.css";
import { UiButton } from "~/components/ui-button";
import { PRODUCT_EVENTS } from "../../product.events";
import { useOutletContext } from "react-router-dom";
import {
  getVariantByToppings,
  priceCalculator,
  priceWithWholesale } from
"@commons-monorepo/index";
import ProductAvailability from "./components/product-availability";
import ProductAddToCart from "./components/product-add-to-cart";
import { PRODUCT_COMPONENTS } from "../../constants/components.constants";
import ReviewComments from "../../components/review-comments/review-comments";
import Star from "@sumerkit/icons/star/ssr";

export const ProductSkin1 = ({
  product,
  country,
  partnerId,
  partnerNetwork,
  dbProducts,
  offerId,
  reviews: newReviews,
  ...props
}: ProductProps) => {
  const valuesStickyArea = useRef(null);
  const valuesStickyObserverRef = useRef<null | IntersectionObserver>(null);
  const { productId } = useAppParams();
  const { storeMetadata, isMobile } = useOutletContext<any>();

  const newReviewsRef = useRef<HTMLDivElement>(null);

  const url = `/catalogo/${partnerNetwork?.urlSummer}`;
  const {
    t,
    reviewsRef,
    theme,
    toppings,
    reviews,
    productsRandom,
    inventoryConfig,
    breadcrumbsRoutes,
    addCardProductFixed,
    hasProductInTheCart,
    componentsForPage,
    logEvent,
    handleShareProduct,
    goToCheckout,
    onViewStoreReviews,
    handleChangeToppings,
    handleToCheckoutPay
  } = useLogicProduct({
    product,
    partnerId,
    partnerNetwork,
    country,
    dbProducts,
    offerId,
    ...props
  });

  const variantProduct = useMemo(
    () =>
    getVariantByToppings(product.productToppingVariations || [], toppings),
    [product, toppings]
  );

  const priceProduct = useMemo(() => {
    const payload: DBProduct = {
      offerId,
      id: product.id,
      quantity: 1,
      toppings: toppings || [],
      variationId: variantProduct?.id,
      price: priceCalculator(product, toppings),
      wholesalePrice: priceWithWholesale(product, toppings),
      wholesale: product.wholesale
    };
    payload.dbId = getDbProductId(payload);
    const productInCart = dbProducts.find((p) => p.dbId === payload.dbId);
    if (productInCart) {
      payload.quantity = productInCart.quantity;
    }
    if (payload.wholesalePrice !== undefined) {
      if (product.productToppingVariations) {
        payload.wholesalePrice =
        payload.quantity >= (
        variantProduct?.wholesale?.configurations[0].minUnits || 0) ?
        payload.wholesalePrice :
        undefined;
      } else {
        payload.wholesalePrice =
        payload.quantity >= (
        product?.wholesale?.configurations[0].minUnits || 0) ?
        payload.wholesalePrice :
        undefined;
      }
    }
    return productPrice(
      payload.price || 0,
      payload.quantity,
      payload.wholesalePrice
    );
  }, [dbProducts, toppings, product, variantProduct]);

  const scrollIntoToppings = () => {
    const element = document.getElementById("product-detail-toppings");
    element?.scrollIntoView(
      isSafari() ? true : { behavior: "smooth", block: "center" }
    );
  };

  useEffect(() => {
    valuesStickyObserverRef.current = new IntersectionObserver(
      ([entry]) => {
        entry.target.classList.toggle(
          "product-detail-value__sticky",
          entry.intersectionRatio < 1
        );
      },
      { threshold: [0, 1] }
    );
  }, []);

  useEffect(() => {
    if (valuesStickyArea.current) {
      valuesStickyObserverRef.current?.observe(valuesStickyArea.current);
    }
    return () => {
      valuesStickyObserverRef.current?.disconnect();
    };
  }, [valuesStickyArea]);

  const isAvailable = product.available;

  const productInventoryRequireToppings = useMemo(
    () =>
    !product.productToppingVariations ||
    product.productToppingVariations && toppings?.length > 0,
    [product, toppings]
  );

  const isInventoryAvailable = useMemo(
    () =>
    inventoryConfig?.type !== TypeInventory.Limited ||
    inventoryConfig?.type === TypeInventory.Limited &&
    inventoryConfig.units > 0 ||
    !productInventoryRequireToppings,
    [inventoryConfig, productInventoryRequireToppings]
  );

  useEffect(() => {
    if (!isInventoryAvailable) {
      if (!(product.productToppingVariations && toppings.length <= 0)) {
        logEvent(PRODUCT_EVENTS.VIEW_SCREEN, {
          user_type: "buyer",
          platform: "web",
          event_subtype: PRODUCT_EVENTS.OUT_OF_STOCK,
          product_id: product.id,
          variation_id: variantProduct?.id || ""
        });
      }
    }
  }, [isInventoryAvailable, product, toppings, variantProduct]);

  const ProductDescription = () => {
    return (
      <div
        className={`${styles.productDetailDescription}`}
        aria-label='product-detail-description'>

        <h3 className={`${styles.productDetailSectionTitle}`}>
          {t("product.details")}
        </h3>
        <ReadMore
          className={`${styles.productDetailDescriptionText}`}
          text={product.description}
          maxLength={300} />

      </div>);

  };

  const ProductComboList = () => {
    return (
      <div
        className={`${styles.productComboDescription}`}
        aria-label='product-detail-description'>

        <h3 className={`${styles.productDetailSectionTitle}`}>
          {t("product.comboDescription")}:
        </h3>
        <ul className={styles.comboList}>
          {(product.products || []).map((productKit, index) =>
          <li key={`product-kit-${index}`}>
              <strong>{productKit.productDetail.name}</strong> (
              {productKit.units} unidades)
            </li>
          )}
        </ul>
      </div>);

  };

  const getGlobalAverage = useMemo(() => {
    let summatory = 0;
    newReviews?.forEach((comment) => {
      summatory += comment.stars;
    });
    return summatory / (newReviews?.length || 0);
  }, [newReviews]);

  const scrollToReviews = () => {
    newReviewsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };

  return (
    <section className={`${styles.productOnixStyled}`}>
      <div
        className={`${styles.productOnixContainer}`}
        aria-label='product-onix-container'>

        <header className={`${styles.productDetailHeader}`}>
          <Breadcrumbs className={"back"} routes={breadcrumbsRoutes} />
        </header>
        <div className={`${styles.productDetailLayout}`}>
          <section className={`${styles.productDetailLayoutLeft}`}>
            <div
              className={`${styles.productDetailLayout__sticky} ${
              theme.template.HEADER.SKIN === TemplateType.HEADER_5 ?
              styles.productDetailLayout__header5 :
              ""
              }`}>

              <div className='product-detail-gallery'>
                <SliderProducts
                  disable={!isAvailable || !isInventoryAvailable}
                  product={product}
                  styleClassName={`${styles.fullWidthSlider}`} />

              </div>
            </div>
            {!isMobile && newReviews && newReviews.length !== 0 &&
            <div className={styles.newReviews} ref={newReviewsRef}>
                <ReviewComments
                globalAverage={getGlobalAverage}
                comments={newReviews || []}
                t={t}
                isMobile={isMobile} />

              </div>}

          </section>
          <section className={`${styles.productDetailLayoutRight}`}>
            <article
              className={`${styles.productDetailSection} ${styles.productDetailInfo}`}>

              {componentsForPage.map((component, index) =>
              <Fragment key={`product-component-${index}`}>
                  {component === PRODUCT_COMPONENTS.TITLE &&
                <>
                      <ProductCloudTitle
                    className={`${styles.productCloudTitleContainer}`}
                    title={product.name}
                    onShareProduct={handleShareProduct} />

                      {newReviews && newReviews.length !== 0 &&
                  <div className={styles.calificationReview}>
                          <div className={styles.stars}>
                            <Star weight={"fill"} width='16px' height='16px' />
                            <div className={styles.average}>
                              {getGlobalAverage}
                            </div>
                          </div>
                          <div className={styles.separator}>•</div>
                          <div
                      className={styles.text}
                      onClick={scrollToReviews}>

                            ({newReviews.length}{" "}
                            {newReviews.length === 1 ?
                      t("orderReview.reviewProductDetail.review") :
                      t("orderReview.reviewProductDetail.reviews")}
                            )
                          </div>
                        </div>}

                    </>}


                  {component === PRODUCT_COMPONENTS.TAGS && (
                product?.highlight ||
                product?.kit ||
                product?.wholesale?.configurations) &&
                <ProductCloudTags
                  className={`${styles.productDetailTags}`}
                  product={product} />}


                  {component === PRODUCT_COMPONENTS.PRICES &&
                <ProductCloudPrices
                  className={`${styles.productDetailPrices}`}
                  product={product}
                  toppings={toppings}
                  country={country} />}


                  {priceProduct > 0 &&
                component === PRODUCT_COMPONENTS.ADD_TO_CART &&
                addCardProductFixed &&
                <ProductAddToCart
                  product={product}
                  selectedToppings={toppings}
                  hasProductInTheCart={hasProductInTheCart}
                  disabled={!isAvailable || !isInventoryAvailable}
                  offerId={offerId}
                  theme={theme}
                  onRequiredTopping={scrollIntoToppings}
                  onGoToCheckoutCart={goToCheckout}
                  onGoToCheckoutPay={handleToCheckoutPay} />}


                  {component === PRODUCT_COMPONENTS.DESCRIPTION &&
                product?.description && <ProductDescription />}

                  {component === PRODUCT_COMPONENTS.COMBO_LIST &&
                !!product?.kit && <ProductComboList />}

                  {component === PRODUCT_COMPONENTS.TOPPINGS &&
                product?.toppings &&
                <div
                  id='product-detail-toppings'
                  className='product-detail-toppings'
                  aria-label='product-detail-toppings'>

                        <Toppings
                    toppings={product.toppings}
                    productVariations={product.productToppingVariations}
                    handleToppings={handleChangeToppings} />

                      </div>}

                  {component === PRODUCT_COMPONENTS.ADDITIONAL_INFO &&
                product?.complementaryContent?.enabled &&
                <AppLink
                  to={"informacion-adicional"}
                  onClick={() => {
                    logEvent(PRODUCT_EVENTS.SELECT_LINK, {
                      user_type: "buyer",
                      platform: "web",
                      event_subtype:
                      PRODUCT_EVENTS.PRODUCT_ADDITIONAL_CONTENT,
                      product_id: product.id
                    });
                  }}>

                        <div className={`${styles.complementaryContentLink}`}>
                          {product?.complementaryContent?.title}
                        </div>
                      </AppLink>}

                  {component === PRODUCT_COMPONENTS.AVAILABILITY &&
                <ProductAvailability
                  product={product}
                  inventoryConfig={inventoryConfig}
                  isInventoryAvailable={isInventoryAvailable} />}


                </Fragment>
              )}
            </article>
            {reviews?.length > 0 &&
            <section className={`${styles.productDetailSection}`}>
                <div className='productDetailSection-content'>
                  <h3 className={`${styles.productDetailSectionTitle}`}>
                    {t("reviews.product_rating")}
                  </h3>
                  <div
                  className={"product-detail-reviews"}
                  ref={reviewsRef}
                  aria-label='product-detail-reviews'>

                    <Reviews
                    reviews={reviews}
                    source={ProductInfoSource.ProductPage}
                    name={product.name}
                    skin={TemplateType.PRODUCT_REVIEWS_SKIN_2} />

                  </div>
                </div>
              </section>}

            {productsRandom.length > 0 &&
            <section
              className={`${styles.productDetailSection} ${styles.productDetailSection__clean}`}>

                <div className='productDetailSection-content'>
                  <h3 className={`${styles.productDetailSectionTitle}`}>
                    {t("product.recommended")}
                  </h3>
                  <div
                  className={`${styles.productDetailProductsRandom}`}
                  aria-label='product-detail-products-random'>

                    {productsRandom.map((productRandom, index) =>
                  <div
                    className={`${styles.productDetailProductsItem}`}
                    key={`detail-products-item-${index}`}>

                        <ProductCard
                      key={`product-detail-product-${productRandom.id}`}
                      product={productRandom}
                      partnerCountry={country}
                      offerId={offerId}
                      partnerId={(partnerId as string)}
                      dbProducts={dbProducts}
                      skin={theme.template.PRODUCT_DETAIL_CARD} />

                      </div>
                  )}
                  </div>
                </div>
              </section>}

            <div className={`${styles.productDetailSection}`}>
              <div className='productDetailSection-content'>
                <h3 className={`${styles.productDetailSectionTitle}`}>
                  {t("StoreInformation.title")}
                </h3>
                <StoreInfo
                  source={ProductInfoSource.ProductPage}
                  textButton={t("StoreInformation.see_store_reviews")}
                  to={`${url}/informacion`}
                  onViewStoreReviews={onViewStoreReviews}
                  partner={storeMetadata} />

              </div>
            </div>
            {isMobile && newReviews && newReviews.length !== 0 &&
            <div className={styles.newReviews} ref={newReviewsRef}>
                <ReviewComments
                globalAverage={getGlobalAverage}
                comments={newReviews || []}
                t={t}
                isMobile={isMobile} />

              </div>}

            {priceProduct > 0 && !addCardProductFixed &&
            <div
              className={`${styles.productDetailValue}`}
              ref={valuesStickyArea}>

                <div className={`${styles.productDetailSection}`}>
                  <div className={`${styles.productDetailValuePrices}`}>
                    <div className='productDetailValue-text'>
                      <div className={`${styles.productDetailPrice}`}>
                        <h4 className={`${styles.productDetailPriceTitle}`}>
                          {t("product.price")}
                        </h4>
                        <p className={`${styles.productDetailPriceValue}`}>
                          {formatCurrency(priceProduct, country)}
                          {product?.priceWithDiscount &&
                        <span className={`${styles.priceValueDiscount}`}>
                              -{product.percentageDiscount}%
                            </span>}

                        </p>
                      </div>
                    </div>
                    <div className='productDetailValue-selector'>
                      <ProductSelector
                      product={product}
                      offerId={offerId}
                      selectedToppings={toppings}
                      onRequiredTopping={scrollIntoToppings}
                      disable={!isAvailable || !isInventoryAvailable} />

                    </div>
                  </div>
                  {hasProductInTheCart &&
                <div className={`${styles.productDetailValueOptions}`}>
                      <UiButton
                    isFilled
                    hasPersonalization
                    rightIcon={
                    <span
                      className={theme.config.button?.icon || "icon-cart"} />}


                    className={`${styles.productDetailValueOption}`}
                    onClick={() => goToCheckout()}>

                        {t("button.goTo")}
                      </UiButton>
                      <UiButton
                    isSecondary
                    className={`${styles.productDetailValueOption} ${styles.buyNow}`}
                    onClick={handleToCheckoutPay}>

                        {t("button.payment")}
                      </UiButton>
                    </div>}

                </div>
              </div>}

          </section>
        </div>
      </div>
      <SchemaGraph product={product} productId={productId} reviews={reviews} />
    </section>);

};